/**
 * Generic renderer for a worker-reported job summary (RM-117): the free-form
 * `job.job_summary` JSON a worker POSTs at job end. The shape is worker-decided,
 * so this stays type-agnostic:
 *   - a flat object (all values primitive) renders as label/value rows, mirroring
 *     the "Parametry zadania" box;
 *   - anything else (nested values, arrays, bare primitives) falls back to a
 *     formatted JSON block.
 *
 * Distinct from `JobSummary` (RM-118), which summarizes the job *spec* for the
 * list table. This one shows what the worker reported after running.
 */

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function isPrimitive(v: unknown): boolean {
  return v === null || ['string', 'number', 'boolean'].includes(typeof v)
}

export function JobReportSummary({ summary }: { summary: unknown }) {
  if (summary === null || summary === undefined) return null

  const isFlat =
    isPlainObject(summary) && Object.values(summary).every(isPrimitive)

  if (isFlat) {
    const rows = Object.entries(summary as Record<string, unknown>)
    if (rows.length === 0)
      return <p className="text-sm text-muted-foreground">Brak danych</p>
    return (
      <dl className="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-2 text-sm">
        {rows.map(([key, value]) => (
          <div key={key} className="contents">
            <dt className="break-words text-muted-foreground">{key}</dt>
            <dd className="min-w-0 break-words text-foreground">
              {value === null ? '—' : String(value)}
            </dd>
          </div>
        ))}
      </dl>
    )
  }

  return (
    <pre className="overflow-auto rounded bg-black/40 p-3 text-xs text-foreground">
      {JSON.stringify(summary, null, 2)}
    </pre>
  )
}
